import Phaser from 'phaser';
import { isCalmMode, CALM_AUDIO_FACTOR } from './CalmMode.js';

/** Master mix — halves everything so the soundscape isn't blown out. */
const MASTER_MIX = 0.5;

/** Master enable flag — wired to the analog SFX switch in the TV bezel. */
const STORAGE_KEY = 'snake_sfx_enabled';
let enabled: boolean = (() => {
  try { return localStorage.getItem(STORAGE_KEY) !== '0'; }
  catch { return true; }
})();
const listeners: Set<(on: boolean) => void> = new Set();

export function setSfxEnabled(on: boolean): void {
  if (enabled === on) return;
  enabled = on;
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  for (const cb of listeners) cb(on);
}
export function isSfxEnabled(): boolean { return enabled; }
export function onSfxEnabledChange(cb: (on: boolean) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/**
 * Per-key relative multipliers (applied on top of MASTER_MIX).
 * - Values < 1.0 dampen sounds that tend to saturate (percussive, low-end heavy, frequent).
 * - Values > 1.0 boost sounds that need to cut through (voicelines, key feedback).
 * Anything not listed plays at MASTER_MIX (0.5).
 */
const KEY_MIX: Record<string, number> = {
  // Big punchy hits — dampen, they were peaking
  title_slam: 0.6,
  player_death: 0.7,
  bot_death: 0.6,
  chest_crack: 0.7,
  chest_shake: 0.7,
  chest_appear: 0.8,
  reward_legendary: 0.7,
  reward_skin_reveal: 0.8,
  match_end: 0.6,
  death_overlay: 0.7,
  deal_button_press: 0.7,
  extraction_flash: 0.7,
  portal_enter: 0.8,

  // High-frequency / stacking sounds — dampen so chains don't blow out
  coin_pickup: 0.6,
  coin_pickup_chain: 0.65,
  coin_pickup_jackpot: 0.7,
  chain_levelup: 0.7,
  coins_rolling: 0.6,
  coins_punch: 0.6,
  card_deal: 0.7,
  card_land: 0.7,
  card_hover: 0.6,
  card_select: 0.8,
  kill_hit: 0.7,
  countdown_tick: 0.7,
  countdown_go: 0.9,

  // UI — keep restrained, hover spam is annoying
  button_hover: 0.5,
  button_click: 0.7,
  menu_portal_hover: 0.6,
  menu_portal_click: 0.85,
  scene_transition: 0.7,

  // Voicelines — boost so they cut through the mix
  voice_first_blood: 1.4,
  voice_double_kill: 1.4,
  voice_triple_kill: 1.4,
  voice_dominating: 1.4,
  first_blood: 1.1,
  kill_confirmed: 1.0,
  streak_double: 1.1,
  streak_triple: 1.1,
  streak_ultra: 1.2,
  streak_rampage: 1.3,
  streak_godlike: 1.3,
};

/** Centralized sound effect playback — applies master mix + per-key balance. */
export function sfx(scene: Phaser.Scene, key: string, config?: Phaser.Types.Sound.SoundConfig): void {
  if (!enabled) return;
  if (!(scene.sound.get(key) || scene.cache.audio.exists(key))) return;
  const baseVol = config?.volume ?? 1;
  const keyMix = KEY_MIX[key] ?? 1;
  const calmMix = isCalmMode() ? CALM_AUDIO_FACTOR : 1;
  const finalVol = baseVol * keyMix * MASTER_MIX * calmMix;
  scene.sound.play(key, { ...config, volume: finalVol });
}
